import React, { useCallback, useEffect, useMemo, useState } from "react"
import { graphql, navigate, useStaticQuery } from "gatsby"
import { AspectRatio, Box, Button, Flex, Image, Text, useDisclosure } from "@chakra-ui/react"

import { useCart } from "@app/hooks/useCart"
import { useCartItemComputed } from "@app/hooks/useCartItemComputed"
import { useDebounce } from "@app/hooks/useDebounce"
import { useShopify, useShopifyPrice } from "@app/hooks/useShopify"
import { useRoutes } from "@app/hooks/useRoutes"
import { useConfigContext } from "@app/providers/config"
import { useCore } from "@app/hooks/useCore"
import Link from "@app/components/Link"
import Icon from "@app/components/Icon"
import ResolvedContent from "@app/components/ResolvedContent"
import Discount from "@app/components/Discount"
import { LowStockMessage } from "@app/components/LowStockMessage"
import CartItemBundle from "@app/components/Cart/CartItemBundle"
import { CartLine, ComponentizableCartLine } from "@shopify/hydrogen-react/storefront-api-types"
import { isGiftBundle } from "@app/utils/bundle"
import { useGlobalContext } from "@app/providers/global"
import config from "../../../config"
import { useAppContext } from "@app/providers/app"
import ProductCardWishlist from "@app/components/ProductCard/ProductCardWishlist"
import { useLowStockThreshold } from "@app/hooks/useLowStockThreshold"

const ProductCardWishlistAnim = React.lazy(() => import("@app/components/ProductCard/ProductCardWishlistAnim"))

type BundleInfo = {
  title: string
  image: string
  handle: string
  totalPrice: number
  tags?: string[]
}

type Props = {
  item: CartLine | ComponentizableCartLine
  /** Bundle children passed from CartList (grouped by _gift_bundle_id) when Cart Transform merge is disabled */
  bundleChildren?: CartLine[]
  /** Bundle product info for display (title, image, handle) when using virtual bundle parent */
  bundleInfo?: BundleInfo
}

export const CartItem: React.FC<Props> = ({ item, bundleChildren, bundleInfo }) => {
  const { giftWithPurchase, cart: cartTemplate } = useStaticQuery<GatsbyTypes.StaticCartItemQuery>(graphql`
    query StaticCartItem {
      giftWithPurchase: sanitySettingGwp {
        additionalGift
        allowMultipleGifts
      }
      cart: sanityPageCart {
        additionalUnavailableLabel
      }
    }
  `)

  const {
    settings: { cart, routes, params, product },
  } = useConfigContext()
  const {
    helpers: { decodeShopifyId, isBrowser },
  } = useCore()
  const [quantity, setQuantity] = useState<number>(Number(item.quantity))
  const { recipientName, recipientEmail, recipientDeliveryDate, recipientMessage } = useCartItemComputed(item)
  const { loading, removeFromCart, updateQuantity, giftWrapping } = useCart()
  const { price, formattedPrice, onSale: productOnSale, compareAtPrice } = useShopifyPrice(item.merchandise, item.quantity)
  const { urlResolver } = useRoutes()
  const { formatMoney } = useShopify()
  const { additionalBundleAccordion } = useGlobalContext()
  const { isOpen: bundleItemsOpen, onToggle: toggleBundleItemsDisclosure } = useDisclosure()
  const { dispatch } = useAppContext()
  const [shouldPlayWishlistAnim, setShouldPlayWishlistAnim] = useState(false)
  const wishlistRef = React.useRef<HTMLDivElement>(null)

  // Bundle detection: either product has giftBundle tag (merged bundles) OR has bundleChildren (grouped by _gift_bundle_id)
  const isBundle = isGiftBundle(item?.merchandise?.product) || (bundleChildren && bundleChildren.length > 0)

  // Calculate bundle subtotal (original price before discounts) for strikethrough display
  const bundleSubtotal = useMemo(() => {
    if (!bundleChildren?.length) return 0
    return bundleChildren.reduce((sum, child) => sum + Number(child.cost?.subtotalAmount?.amount || 0), 0)
  }, [bundleChildren])

  // Bundle is "on sale" if subtotal > total (meaning discounts were applied)
  const bundleHasDiscount = useMemo(() => {
    if (!isBundle || !bundleInfo?.totalPrice) return false
    return bundleSubtotal > bundleInfo.totalPrice
  }, [isBundle, bundleSubtotal, bundleInfo?.totalPrice])

  useEffect(() => {
    // Reset quantity if it changes in the cart
    setQuantity(Number(item.quantity))
  }, [item.quantity])

  const isDiscount = Number(item.cost.subtotalAmount.amount) > Number(item.cost.totalAmount.amount)
  const onSale = useMemo(() => {
    if (isBundle) return bundleHasDiscount
    return productOnSale || isDiscount
  }, [isBundle, bundleHasDiscount, productOnSale, isDiscount])

  const isGiftWrap = useMemo(
    () => item.attributes?.some(({ key }: { key: string }) => key === cart.giftWrapAttribute),
    [item.attributes, cart.giftWrapAttribute]
  )
  const isGwp = useMemo(
    () => item.attributes?.some(({ key }: { key: string }) => key === cart.gwpAttribute),
    [item.attributes, cart.gwpAttribute]
  )
  // When multi-mode is on a gift can be added more than once, so its line gets the
  // normal quantity selector. In single-pick mode gift quantity is fixed.
  const allowMultipleGifts = useMemo(() => !!giftWithPurchase?.allowMultipleGifts, [giftWithPurchase?.allowMultipleGifts])
  const gwpPercentage = useMemo(() => {
    const value = item.attributes?.find(({ key }: { key: string }) => key === cart.gwpPercentage)?.value
    const parsed = parseInt(value ?? "", 10)
    return Number.isFinite(parsed) && parsed >= 1 && parsed <= 99 ? parsed : null
  }, [item.attributes, cart.gwpPercentage])
  const isDonation = useMemo(() => item?.merchandise?.product?.tags?.includes("donation-product"), [item.merchandise.product.tags])

  const isOutOfStock = useMemo(() => !item?.merchandise?.availableForSale, [item?.merchandise?.availableForSale])

  const isGiftcard = useMemo(
    () => item.merchandise?.product?.handle?.toLowerCase() === product.giftcardHandle,
    [item.merchandise?.product?.handle, product.giftcardHandle]
  )
  const additionalTextAttribute = item.attributes?.find(attribute => attribute.key === cart.giftUpsellText)
  const additionalTextColorAttribute = item.attributes?.find(attribute => attribute.key === cart.giftUpsellTextColor)

  const handleQuantity = useCallback((quantity: number) => setQuantity(quantity), [setQuantity])

  const url = useMemo(
    () =>
      `${urlResolver(item.merchandise?.product, routes.PRODUCT)?.url}?${params.variant}=${decodeShopifyId(
        item.merchandise?.id,
        "ProductVariant"
      )}`,
    [decodeShopifyId, item, params.variant, routes, urlResolver]
  )

  // Use bundleChildren (from _gift_bundle_id grouping) or lineComponents (from Cart Transform merge)
  const effectiveBundleChildren = bundleChildren || (item as ComponentizableCartLine).lineComponents

  const quantityAvailable = useMemo(() => {
    if (effectiveBundleChildren?.length) {
      // Get minimum quantity of all children which dictates how many of the bundle can be sold
      return effectiveBundleChildren.reduce((min: number, component: CartLine) => {
        const quantityAvailable = component.merchandise?.quantityAvailable || 0
        if (min === -1 || quantityAvailable < min) {
          return quantityAvailable
        }
        return min
      }, -1)
    } else {
      // Not a bundle
      return item.merchandise?.quantityAvailable || 0
    }
  }, [effectiveBundleChildren, item.merchandise?.quantityAvailable])

  const isIncreaseQuantityDisabled = useMemo(() => {
    if (!quantity || loading) {
      return true
    }

    // Quantity available undefined, exclude when it is defined as 0
    if (!quantityAvailable && quantityAvailable !== 0) {
      return true
    }

    return quantity >= quantityAvailable
  }, [loading, quantity, quantityAvailable])

  const { lowStockSettings, showLowStockMessage } = useLowStockThreshold(quantityAvailable, "cart")

  useDebounce(
    () => {
      if (quantity !== Number(item.quantity)) {
        const action = quantity > item.quantity ? "add" : "remove"
        quantity ? updateQuantity(item.id, item.merchandise?.id, quantity, action) : removeFromCart(item.id, item.merchandise?.id)
      }
    },
    500,
    [quantity]
  )

  const handleBundleEdit = useCallback(() => {
    // Use bundleInfo.handle for virtual bundle parent, fall back to item handle for merged bundles
    const bundleHandle = bundleInfo?.handle || item.merchandise.product.handle
    // Get the bundle ID from attributes (the unique _gift_bundle_id value)
    const bundleId = item.attributes?.find(a => a.key === cart.bundleIdAttribute)?.value
    if (!bundleId) return
    navigate(config.settings.routes.BUNDLE + "/" + bundleHandle + "?edit=" + btoa(bundleId))

    // close the cart drawer
    dispatch({
      type: "cart",
      payload: false,
    })
  }, [bundleInfo?.handle, cart.bundleIdAttribute, dispatch, item.attributes, item.merchandise.product.handle])

  return item ? (
    <>
      <Flex pos="relative" w="100%" pt={4} pb={5} borderBottom="1px solid" borderBottomColor="primary.softGrey">
        <Box w={{ base: "100px", sm: "112px" }} flexShrink={0} pos="relative">
          {(bundleInfo?.image || item.merchandise?.image) && (
            <Box as={!isGiftWrap && !isDonation ? Link : "span"} {...(!isGiftWrap && !isDonation ? { to: url } : {})}>
              <AspectRatio ratio={[100 / 125]}>
                <Image
                  src={bundleInfo?.image || item.merchandise?.image?.originalSrc}
                  w="100%"
                  h="100%"
                  objectFit="cover"
                  borderRadius="8px"
                />
              </AspectRatio>
            </Box>
          )}
          {!(isGiftcard || isGiftWrap || isDonation || isBundle) && (
            <>
              <Box pos="absolute" top="0" right="0" zIndex={2}>
                <Box ref={wishlistRef}>
                  <ProductCardWishlist
                    product={item.merchandise}
                    variant="cart"
                    location="cart"
                    handleAdd={() => setShouldPlayWishlistAnim(true)}
                  />
                </Box>
              </Box>
              {isBrowser && (
                // Add state and ref for wishlist animation
                <React.Suspense fallback={<div />}>
                  <ProductCardWishlistAnim
                    posRef={wishlistRef}
                    shouldPlay={shouldPlayWishlistAnim}
                    handlePlay={() => setShouldPlayWishlistAnim(false)}
                    variant="cart"
                  />
                </React.Suspense>
              )}
            </>
          )}
          {showLowStockMessage && (
            <Box position="absolute" bottom={2} left="50%" transform="translateX(-50%)" zIndex={98}>
              <LowStockMessage settings={lowStockSettings} />
            </Box>
          )}
        </Box>

        <Box flex={1} pl={3}>
          <Flex justifyContent="space-between" h="100%" flexDir="column">
            <Box>
              <Flex gap={2} alignItems="flex-start" pt={3} flexWrap="wrap">
                <Box flex={1} minW={{ base: "55%", sm: "unset" }}>
                  <Text size="lg" maxW={{ base: "12ch", sm: "unset" }} sx={{ textWrap: "pretty" }}>
                    <Box
                      as={!isGiftWrap && !isDonation ? Link : "span"}
                      _hover={{ textDecoration: "none" }}
                      {...(!isGiftWrap && !isDonation ? { to: url } : {})}
                    >
                      {bundleInfo?.title || item.merchandise.product.title}
                    </Box>
                  </Text>
                </Box>
                <Flex
                  flexDir={{ base: "column-reverse", sm: "row" }}
                  alignItems={{ base: "flex-end", sm: "baseline" }}
                  gap={{ base: 0, sm: 2 }}
                  flexShrink={0}
                  py="2px"
                >
                  {isGwp ? (
                    gwpPercentage ? (
                      <>
                        <Text size="lg" color="text.error">
                          {formatMoney(item.cost.totalAmount.amount)}
                        </Text>
                        <Text size="lg" textDecoration="line-through" color="secondary.grey">
                          {formattedPrice}
                        </Text>
                      </>
                    ) : (
                      <>
                        <Text size="lg" textDecoration="line-through" color="secondary.grey">
                          {formattedPrice}
                        </Text>
                        <Text size="lg" color="text.error">
                          {giftWithPurchase?.additionalGift}
                        </Text>
                      </>
                    )
                  ) : (
                    <>
                      {onSale && compareAtPrice !== undefined && (
                        <Text size="lg" textDecoration="line-through" color="secondary.grey">
                          {isBundle
                            ? formatMoney(bundleSubtotal)
                            : item?.discountAllocations?.length
                              ? formatMoney(Math.max(price * item.quantity, Number(item.cost.subtotalAmount.amount)))
                              : formatMoney(Math.max(compareAtPrice * item.quantity, Number(item.cost.subtotalAmount.amount)))}
                        </Text>
                      )}
                      <Text size="lg" color={onSale ? "text.error" : "primary.midGrey"}>
                        {isBundle
                          ? formatMoney(bundleInfo?.totalPrice ?? item.cost.totalAmount.amount)
                          : isDiscount
                            ? formatMoney(item.cost.totalAmount.amount)
                            : formattedPrice}
                      </Text>
                    </>
                  )}
                </Flex>
              </Flex>

              {!isBundle
                ? item.merchandise?.title &&
                  !isDonation && (
                    <Text mt={1} size="sm">
                      {isGiftcard ? item.merchandise.title.split("/")[0].trim() : item.merchandise.title}
                    </Text>
                  )
                : null}

              {additionalTextAttribute && (
                <Text mt={1} size="sm" textTransform="uppercase" variant="medium" color={additionalTextColorAttribute?.value || "current"}>
                  {additionalTextAttribute.value}
                </Text>
              )}

              {isOutOfStock && (
                <Text mt={1} size="sm" color="preset.red">
                  {cartTemplate?.additionalUnavailableLabel}
                </Text>
              )}

              {!isGwp && <Discount tags={bundleInfo?.tags || item.merchandise.product?.tags} size="sm" mt={1} forceColor="text.error" />}

              {isGiftWrap && giftWrapping?.content && (
                <ResolvedContent
                  mt={2}
                  content={giftWrapping.content}
                  color="text.disabled"
                  sx={{
                    ".content-p": {
                      fontSize: "xs",
                      marginTop: "4px",
                    },
                  }}
                />
              )}

              {isBundle && (
                <Button variant="text" size="small" py={0} onClick={handleBundleEdit}>
                  Edit
                </Button>
              )}

              <Box mt={2}>
                {!!recipientName && (
                  <Text size="sm" my={0}>
                    Recipient Name: {recipientName}
                  </Text>
                )}
                {!!recipientEmail && (
                  <Text size="sm" my={0}>
                    Recipient Email: {recipientEmail}
                  </Text>
                )}
                {!!recipientDeliveryDate && (
                  <Text size="sm" my={0}>
                    Delivery Date: {recipientDeliveryDate}
                  </Text>
                )}
                {!!recipientMessage && (
                  <Text size="sm" my={0}>
                    Gift Message: {recipientMessage}
                  </Text>
                )}
              </Box>
            </Box>

            <Flex alignItems="flex-end" justifyContent="space-between" mt={3}>
              <Box>
                {!recipientName && !isGiftWrap && (!isGwp || allowMultipleGifts) && !isBundle && (
                  <Flex alignItems="center" justifyContent="space-between">
                    <Button
                      variant="cart"
                      isDisabled={!quantity || loading || isOutOfStock}
                      onClick={() => handleQuantity(quantity - 1)}
                      aria-label="Decrease Quantity"
                      title="Decrease Quantity"
                    >
                      <Icon name="minus" width="8px" height="auto" />
                    </Button>
                    <Text textAlign="center" p={2} size="sm" bg="grey.lightest" w={12} overflow="hidden">
                      {quantity}
                    </Text>
                    <Button
                      variant="cart"
                      isDisabled={!quantity || isIncreaseQuantityDisabled || loading || isOutOfStock}
                      onClick={() => {
                        if (!quantity || isIncreaseQuantityDisabled || loading || isOutOfStock) return
                        handleQuantity(quantity + 1)
                      }}
                      aria-label="Increase Quantity"
                      title="Increase Quantity"
                    >
                      <Icon name="plus" width="8px" height="auto" />
                    </Button>
                  </Flex>
                )}
              </Box>
              <Button
                variant="unstyled"
                display="flex"
                alignItems="flex-end"
                minW="auto"
                h="auto"
                isDisabled={loading}
                onClick={() => removeFromCart(item.id, item.merchandise?.id)}
                title="Remove from bag"
              >
                <Text size="sm" color="text.secondary" textDecoration="underline">
                  Remove
                </Text>
              </Button>
            </Flex>

            {isBundle && (
              <Box w="100%">
                <Button padding="0" _hover={{ backgroundColor: "primary.white" }} onClick={toggleBundleItemsDisclosure}>
                  <Text size="sm" marginRight="6px">
                    {additionalBundleAccordion}
                  </Text>
                  <Icon name="chevronDownDropdown" width="10px" transform={bundleItemsOpen ? "rotate(180deg)" : "none"} />
                </Button>
              </Box>
            )}
          </Flex>
        </Box>
      </Flex>

      {isBundle && <CartItemBundle lineComponents={effectiveBundleChildren} isOpen={bundleItemsOpen} />}
    </>
  ) : null
}
